import React from "react";
import {TicketData, UserDataEntity} from "../data/entities/UserDataEntity";
import {UserLocationEntity} from "../data/entities/UserLocationEntity";
import IpLocationService from "../data/services/IpLocationService";
import update from 'immutability-helper';
import {UserProgressionInfoEntity} from "../data/entities/UserProgressionInfoEntity";
import {ProfileEntity, profilePlatformTypes} from "../data/entities/Connect/ProfileEntity";
import {AdobeAnalyticsContext} from "./AdobeAnalyticsContext";

export interface UserContextData {
    user: UserDataEntity;
    userLocation: UserLocationEntity;
    userProgression: UserProgressionInfoEntity | null;
    login: (ticketData: TicketData, profiles: ProfileEntity[], callback?: () => void) => void;
    logout: (callback?: () => void) => void;
    validateUserCountry: (allowedCountries?: string) => boolean;
    validateProgressionType: (allowedProgressionTypes: string[]) => boolean;
    validateStoreReference: (allowedStorefronts: string[]) => boolean;
    validateAnywhereAccess: () => boolean;
    validateSubscriptionProgressionPrivilege: () => boolean;
    validateSubscriptionVaultPrivilege: () => boolean;
    setSubscriptionStatus: (status: boolean, userProgressionInfo: UserProgressionInfoEntity | null) => void;
    authVerified: boolean;
    subscriptionVerified: boolean;
}

const guestUser: UserDataEntity = {
    isAuthenticated: false,
    isSubscribed: false,
    ticketData: {
        expiration: null,
        sessionId: null,
        userId: null,
        ticket: null
    },
    username: null,
    profiles: [],
};

const userLocation: UserLocationEntity = {
    countryCode: ""
};

export const UserContext = React.createContext({} as UserContextData);

export default class UserProvider extends React.Component<object, UserContextData> {
    static contextType = AdobeAnalyticsContext;
    context!: React.ContextType<typeof AdobeAnalyticsContext>;

    login = (ticketData: TicketData, profiles: ProfileEntity[], callback = () => {}) => {
        let uplayProfile = profiles.find(profile => profile.platformType === profilePlatformTypes.uplay);
        let username: string = uplayProfile ? uplayProfile.nameOnPlatform : "";

        this.context.track("accountevent", {
            ubisoftId: ticketData.userId,
            loggedIn: "true",
            loggedOut: "false",
            action: "page - login - signed in"
        });

        this.setState(prevState => ({
            user: update(prevState.user, {
                isAuthenticated: {$set: true},
                ticketData: {$set: ticketData},
                username: {$set: username},
                profiles: {$set: profiles},
            }),
            authVerified: true
        }), callback);
    };

    logout = (callback = () => {}) => {
        this.setState({user: guestUser, authVerified: true}, callback);
    };

    setSubscriptionInfo = (status: boolean, userProgressionInfo: UserProgressionInfoEntity | null) => {
        this.setState(prevState => ({
            user: update(prevState.user, {
                isSubscribed: {$set: status}
            }),
            userProgression: userProgressionInfo,
            subscriptionVerified: true
        }));
    };

    validateUserCountry = (allowedCountries?: string): boolean => {
        if (!allowedCountries) {
            return true;
        }

        return (
            this.state.userLocation.countryCode !== "" &&
            allowedCountries.indexOf(this.state.userLocation.countryCode) !== -1
        );
    };

    validateProgressionType = (allowedProgressionTypes: string[]): boolean => {
        return (
            this.state.userProgression !== null &&
            this.state.userProgression.currentProgressionLevel !== null &&
            allowedProgressionTypes.includes(this.state.userProgression.subscriptionInfo.type)
        );
    };

    validateStoreReference = (allowedStorefronts: string[]): boolean => {
        return (
            this.state.userProgression !== null &&
            allowedStorefronts.includes(this.state.userProgression.subscriptionInfo.storeReference)
        );
    };

    validateAnywhereAccess = (): boolean => {
        return (
            this.state.user.isSubscribed &&
            this.validateStoreReference(["US"])
        );
    };

    validateSubscriptionProgressionPrivilege = (): boolean => {
        return (
            this.state.userProgression !== null &&
            this.state.userProgression.subscriptionInfo.subscriptionPrivilege !== null &&
            this.state.userProgression.subscriptionInfo.subscriptionPrivilege.progression
        );
    };

    validateSubscriptionVaultPrivilege = (): boolean => {
        return (
            this.state.userProgression !== null &&
            this.state.userProgression.subscriptionInfo.subscriptionPrivilege !== null &&
            this.state.userProgression.subscriptionInfo.subscriptionPrivilege.vault
        );
    };

    state: UserContextData = {
        user: guestUser,
        userLocation: userLocation,
        userProgression: null,
        login: this.login,
        logout: this.logout,
        validateUserCountry: this.validateUserCountry,
        validateProgressionType: this.validateProgressionType,
        validateStoreReference: this.validateStoreReference,
        validateAnywhereAccess: this.validateAnywhereAccess,
        validateSubscriptionProgressionPrivilege: this.validateSubscriptionProgressionPrivilege,
        validateSubscriptionVaultPrivilege: this.validateSubscriptionVaultPrivilege,
        setSubscriptionStatus: this.setSubscriptionInfo,
        authVerified: false,
        subscriptionVerified: false
    };

    componentDidMount = () => {
        this.getUserCountry().then(country => {
            this.setState({userLocation: {countryCode: country}})
        });
    };

    getUserCountry = async(): Promise<string> => {
        let countryCode: string = "";

        let ipLocation = await IpLocationService.getIpLocation();
        if (ipLocation.status === 200) {
            countryCode = ipLocation.data.countryCode;
        }

        return countryCode;
    };

    render(): JSX.Element {
        return (
            <UserContext.Provider value={this.state}>
                {this.props.children}
            </UserContext.Provider>
        );
    }
}